import React, { useState } from "react";
import { Link } from "react-router-dom";
import { getConsultationPath } from "../../../utils/consultationLink";
import { WEB_LEAD_MODULES } from "../../../constants/webLeadModules";
import {
  ArrowLeft,
  Calculator,
  RotateCcw,
  IndianRupee,
  Percent,
  Clock,
  Flame,
  TrendingDown,
  ShoppingCart,
  Home,
  GraduationCap,
  Car,
  Utensils,
  Plane,
} from "lucide-react";
import Navbar from "../../Navbar/Navbar";
import Footer from "../../Footer/Footer";
import Swal from "sweetalert2";
import "./InflationCalculator.css";

/* ─── Calculator modes ─── */
const CALCS = [
  {
    value: "future",
    label: "Future Cost",
    fullLabel: "Future Cost of an Item",
    desc: "How much will ₹X cost after N years?",
    color: "#ef4444",
    colorEnd: "#fca5a5",
    Icon: TrendingDown,
  },
  {
    value: "present",
    label: "Today's Worth",
    fullLabel: "Present Value of Future Money",
    desc: "What is a future amount worth today?",
    color: "#f59e0b",
    colorEnd: "#fcd34d",
    Icon: IndianRupee,
  },
  {
    value: "real",
    label: "Real Returns",
    fullLabel: "Real Rate of Return",
    desc: "Investment return after inflation erosion",
    color: "#2563eb",
    colorEnd: "#60a5fa",
    Icon: Flame,
  },
  {
    value: "goal",
    label: "Goal Inflation",
    fullLabel: "Inflation-Adjusted Goal Amount",
    desc: "How much to save for a future goal?",
    color: "#10b981",
    colorEnd: "#6ee7b7",
    Icon: ShoppingCart,
  },
];

const BASKETS = [
  { icon: Home, label: "Home", amount: 5000000 },
  { icon: GraduationCap, label: "Education", amount: 1000000 },
  { icon: Car, label: "Car", amount: 1200000 },
  { icon: Utensils, label: "Monthly Bills", amount: 30000 },
  { icon: Plane, label: "Travel", amount: 200000 },
];

const formatINR = (n) => {
  if (isNaN(n) || n === null) return "-";
  return Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

const inCrLakh = (n) => {
  if (n >= 10000000) return `\u20b9${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `\u20b9${(n / 100000).toFixed(2)} L`;
  return `\u20b9${formatINR(Math.round(n))}`;
};

const validate = (...vals) =>
  vals.every((v) => v !== "" && !isNaN(+v) && +v >= 0);

export default function InflationCalculator() {
  const [calcType, setCalcType] = useState("future");
  const [amount, setAmount] = useState("");
  const [inflation, setInflation] = useState("");
  const [years, setYears] = useState("");
  const [returnRate, setReturnRate] = useState("");
  const [result, setResult] = useState(null);

  const active = CALCS.find((c) => c.value === calcType);
  const accentColor = active?.color || "#ef4444";
  const accentEnd = active?.colorEnd || "#fca5a5";

  /* ── Calculations ── */
  const calcFutureCost = () => {
    if (!validate(amount, inflation, years)) return null;
    const P = +amount,
      i = +inflation / 100,
      n = +years;
    const future = P * Math.pow(1 + i, n);
    const increase = future - P;
    const pctIncrease = (increase / P) * 100;

    const breakdown = [];
    for (let y = 1; y <= n; y++) {
      const cost = P * Math.pow(1 + i, y);
      breakdown.push({
        year: y,
        cost: Math.round(cost),
        increase: Math.round(cost - P),
      });
    }

    return {
      type: "future",
      P,
      future,
      increase,
      pctIncrease,
      breakdown,
    };
  };

  const calcPresentValue = () => {
    if (!validate(amount, inflation, years)) return null;
    const F = +amount,
      i = +inflation / 100,
      n = +years;
    const present = F / Math.pow(1 + i, n);
    const erosion = F - present;

    const breakdown = [];
    for (let y = 1; y <= n; y++) {
      const pv = F / Math.pow(1 + i, y);
      breakdown.push({
        year: y,
        pv: Math.round(pv),
        erosion: Math.round(F - pv),
      });
    }

    return {
      type: "present",
      F,
      present,
      erosion,
      pct: (erosion / F) * 100,
      breakdown,
    };
  };

  const calcRealReturn = () => {
    if (!validate(amount, returnRate, inflation, years)) return null;
    const P = +amount,
      r = +returnRate / 100,
      i = +inflation / 100,
      n = +years;
    const realRate = ((1 + r) / (1 + i) - 1) * 100;
    const nominalFV = P * Math.pow(1 + r, n);
    const realFV = P * Math.pow(1 + realRate / 100, n);
    const erosion = nominalFV - realFV;

    const breakdown = [];
    for (let y = 1; y <= n; y++) {
      const nom = P * Math.pow(1 + r, y);
      const real = P * Math.pow(1 + realRate / 100, y);
      breakdown.push({
        year: y,
        nominal: Math.round(nom),
        real: Math.round(real),
        erosion: Math.round(nom - real),
      });
    }

    return {
      type: "real",
      P,
      nominalFV,
      realFV,
      erosion,
      realRate,
      nominalRate: +returnRate,
      breakdown,
    };
  };

  const calcGoalInflation = () => {
    if (!validate(amount, inflation, returnRate, years)) return null;
    const G = +amount,
      i = +inflation / 100,
      r = +returnRate / 100,
      n = +years;

    const inflatedGoal = G * Math.pow(1 + i, n);
    const rm = r / 12,
      months = n * 12;
    const sip = (inflatedGoal * rm) / (Math.pow(1 + rm, months) - 1);

    const breakdown = [];
    let sipBal = 0;
    for (let y = 1; y <= n; y++) {
      const currentGoal = G * Math.pow(1 + i, y);
      for (let m = 0; m < 12; m++) {
        sipBal += sip;
        sipBal += sipBal * rm;
      }
      breakdown.push({
        year: y,
        goal: Math.round(currentGoal),
        sipBal: Math.round(sipBal),
      });
    }

    return {
      type: "goal",
      G,
      inflatedGoal,
      sip,
      totalInvested: sip * months,
      growth: inflatedGoal - sip * months,
      breakdown,
    };
  };

  const handleCalculate = () => {
    let output = null;
    if (calcType === "future") output = calcFutureCost();
    if (calcType === "present") output = calcPresentValue();
    if (calcType === "real") output = calcRealReturn();
    if (calcType === "goal") output = calcGoalInflation();
    if (!output) {
      Swal.fire({
        title: "Missing Details",
        text: "Please fill in all required fields with valid values.",
        icon: "warning",
        confirmButtonText: "Got it",
        confirmButtonColor: accentColor,
        width: 380,
      });
      return;
    }
    setResult(output);
  };

  const handleReset = () => {
    setAmount("");
    setInflation("");
    setYears("");
    setReturnRate("");
    setResult(null);
  };

  const isFuture = calcType === "future";
  const isPresent = calcType === "present";
  const isReal = calcType === "real";
  const isGoal = calcType === "goal";

  return (
    <div className="ic-wrapper">
      <Navbar />

      {/* ══ HERO ══ */}
      <section className="ic-hero">
        <div className="ic-hero-bg" />
        <div className="ic-hero-dots" />
        <div className="ic-hero-glow ic-glow-l" />
        <div className="ic-hero-glow ic-glow-r" />

        <div className="ic-hero-inner">
          <Link to="/calculators" className="ic-back-btn">
            <ArrowLeft size={14} /> Back to Calculators
          </Link>

          <div className="ic-hero-top">
            <div>
              <div className="ic-eyebrow">
                <Flame size={12} /> Inflation Calculator
              </div>
              <h1 className="ic-hero-title">
                See What Inflation
                <br />
                <span
                  className="ic-gradient-text"
                  style={{
                    backgroundImage: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                >
                  Really Costs You
                </span>
              </h1>
              <p className="ic-hero-sub">
                Understand how inflation erodes your purchasing power — and how
                much you actually need to invest to stay ahead of rising prices.
              </p>
            </div>
            <div
              className="ic-hero-icon-box"
              style={{
                background: `linear-gradient(135deg, ${accentColor}, ${accentEnd})`,
              }}
            >
              {active ? (
                <active.Icon size={36} color="#fff" />
              ) : (
                <Flame size={36} color="#fff" />
              )}
            </div>
          </div>

          {/* Mode chips */}
          <div className="ic-type-chips">
            {CALCS.map(({ value, label, desc, color, Icon }) => (
              <button
                key={value}
                className={`ic-chip${calcType === value ? " ic-chip--active" : ""}`}
                style={
                  calcType === value
                    ? { borderColor: color, background: `${color}14`, color }
                    : {}
                }
                onClick={() => {
                  setCalcType(value);
                  setResult(null);
                }}
              >
                <Icon size={13} />
                <span>{label}</span>
                <span className="ic-chip-sub">{desc}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ══ MAIN ══ */}
      <div className="ic-content">
        {/* Input Card */}
        <div className="ic-card">
          <div
            className="ic-card-bar"
            style={{
              background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
            }}
          />
          <div className="ic-card-body">
            <div
              className="ic-active-pill"
              style={{
                background: `${accentColor}10`,
                borderColor: `${accentColor}22`,
                color: accentColor,
              }}
            >
              {active && <active.Icon size={13} />}
              {active?.fullLabel} — {active?.desc}
            </div>

            {/* Quick-fill baskets */}
            {(isFuture || isGoal) && (
              <div className="ic-basket-row">
                <span className="ic-basket-label">Quick Fill:</span>
                {BASKETS.map(({ icon: BIcon, label, amount: val }) => (
                  <button
                    key={label}
                    className="ic-basket-chip"
                    style={{
                      borderColor: `${accentColor}30`,
                      color: accentColor,
                    }}
                    onClick={() => {
                      setAmount(String(val));
                      setResult(null);
                    }}
                  >
                    <BIcon size={11} /> {label}
                  </button>
                ))}
              </div>
            )}

            <div className="ic-section-title">
              Input Details
              <span className="ic-section-line" />
            </div>

            <div className="ic-grid-2">
              {isFuture && (
                <>
                  <div className="ic-field">
                    <label className="ic-label">
                      <IndianRupee size={12} /> Current Cost / Amount (&#x20b9;)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 500000"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Flame size={12} /> Inflation Rate (% p.a.)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 6"
                        value={inflation}
                        onChange={(e) => setInflation(e.target.value)}
                      />
                    </div>
                    <span className="ic-hint">India avg CPI ~5–6% p.a.</span>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Clock size={12} /> Years into the Future
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Clock size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 10"
                        value={years}
                        onChange={(e) => setYears(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {isPresent && (
                <>
                  <div className="ic-field">
                    <label className="ic-label">
                      <IndianRupee size={12} /> Future Amount (&#x20b9;)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 1000000"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Flame size={12} /> Inflation Rate (% p.a.)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 6"
                        value={inflation}
                        onChange={(e) => setInflation(e.target.value)}
                      />
                    </div>
                    <span className="ic-hint">India avg CPI ~5–6% p.a.</span>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Clock size={12} /> Years from Now
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Clock size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 10"
                        value={years}
                        onChange={(e) => setYears(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {isReal && (
                <>
                  <div className="ic-field">
                    <label className="ic-label">
                      <IndianRupee size={12} /> Investment Amount (&#x20b9;)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 100000"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Percent size={12} /> Nominal Return Rate (% p.a.)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 12"
                        value={returnRate}
                        onChange={(e) => setReturnRate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Flame size={12} /> Inflation Rate (% p.a.)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 6"
                        value={inflation}
                        onChange={(e) => setInflation(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Clock size={12} /> Duration (Years)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Clock size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 10"
                        value={years}
                        onChange={(e) => setYears(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}

              {isGoal && (
                <>
                  <div className="ic-field">
                    <label className="ic-label">
                      <IndianRupee size={12} /> Today's Goal Amount (&#x20b9;)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <IndianRupee size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 5000000"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Flame size={12} /> Inflation Rate (% p.a.)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 6"
                        value={inflation}
                        onChange={(e) => setInflation(e.target.value)}
                      />
                    </div>
                    <span className="ic-hint">India avg CPI ~5–6% p.a.</span>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Percent size={12} /> Expected Investment Return (% p.a.)
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Percent size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 12"
                        value={returnRate}
                        onChange={(e) => setReturnRate(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ic-field">
                    <label className="ic-label">
                      <Clock size={12} /> Years to Goal
                    </label>
                    <div className="ic-input-wrap">
                      <span className="ic-input-icon">
                        <Clock size={14} />
                      </span>
                      <input
                        type="number"
                        className="ic-input"
                        placeholder="e.g., 15"
                        value={years}
                        onChange={(e) => setYears(e.target.value)}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="ic-btn-row">
              <button
                className="ic-calc-btn"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
                onClick={handleCalculate}
              >
                <Calculator size={16} /> Calculate
              </button>
              <button className="ic-reset-btn" onClick={handleReset}>
                <RotateCcw size={15} /> Reset
              </button>
            </div>
          </div>
        </div>

        {/* ══ RESULTS ══ */}
        {result && (
          <>
            <div className="ic-card">
              <div
                className="ic-card-bar"
                style={{
                  background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                }}
              />
              <div className="ic-card-body">
                <div className="ic-section-title">
                  Result
                  <span className="ic-section-line" />
                </div>

                {/* ── Future Cost ── */}
                {result.type === "future" && (
                  <>
                    <div className="ic-result-hero">
                      <div className="ic-result-col">
                        <div
                          className="ic-result-badge"
                          style={{ background: "#e0f2fe", color: "#0891b2" }}
                        >
                          Today's Value
                        </div>
                        <div
                          className="ic-result-big"
                          style={{ color: "#0891b2" }}
                        >
                          {inCrLakh(result.P)}
                        </div>
                      </div>
                      <div className="ic-result-arrow">&#8594;</div>
                      <div className="ic-result-col">
                        <div
                          className="ic-result-badge"
                          style={{ background: "#fee2e2", color: "#ef4444" }}
                        >
                          After {years} Years
                        </div>
                        <div
                          className="ic-result-big"
                          style={{ color: "#ef4444" }}
                        >
                          {inCrLakh(result.future)}
                        </div>
                      </div>
                    </div>

                    <div className="ic-stat-row">
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Cost Increase</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: "#ef4444" }}
                        >
                          +{inCrLakh(result.increase)}
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">% Rise</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: "#ef4444" }}
                        >
                          +{result.pctIncrease.toFixed(1)}%
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Inflation Rate</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: accentColor }}
                        >
                          {inflation}% p.a.
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Years</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: accentColor }}
                        >
                          {years} yrs
                        </span>
                      </div>
                    </div>

                    <div className="ic-section-title ic-section-title--spaced">
                      Visual Comparison <span className="ic-section-line" />
                    </div>
                    {[
                      {
                        label: "Today's Value",
                        val: result.P,
                        color: "#0891b2",
                      },
                      {
                        label: `After ${years} Yrs`,
                        val: result.future,
                        color: "#ef4444",
                      },
                    ].map((item) => (
                      <div key={item.label} className="ic-bar-group">
                        <div className="ic-bar-row">
                          <span className="ic-bar-label">{item.label}</span>
                          <span className="ic-bar-amt">
                            {inCrLakh(item.val)}
                          </span>
                        </div>
                        <div className="ic-bar-track">
                          <div
                            className="ic-bar-fill"
                            style={{
                              width: `${(item.val / result.future) * 100}%`,
                              background: `linear-gradient(90deg, ${item.color}, ${item.color}88)`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* ── Present Value ── */}
                {result.type === "present" && (
                  <>
                    <div className="ic-result-hero">
                      <div className="ic-result-col">
                        <div
                          className="ic-result-badge"
                          style={{ background: "#fef3c7", color: "#d97706" }}
                        >
                          Future Amount
                        </div>
                        <div
                          className="ic-result-big"
                          style={{ color: "#d97706" }}
                        >
                          {inCrLakh(result.F)}
                        </div>
                      </div>
                      <div className="ic-result-arrow">&#8594;</div>
                      <div className="ic-result-col">
                        <div
                          className="ic-result-badge"
                          style={{ background: "#dcfce7", color: "#16a34a" }}
                        >
                          Worth in Today's Money
                        </div>
                        <div
                          className="ic-result-big"
                          style={{ color: "#16a34a" }}
                        >
                          {inCrLakh(result.present)}
                        </div>
                      </div>
                    </div>

                    <div className="ic-stat-row">
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">
                          Purchasing Power Lost
                        </span>
                        <span
                          className="ic-stat-val"
                          style={{ color: "#ef4444" }}
                        >
                          -{inCrLakh(result.erosion)}
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Erosion %</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: "#ef4444" }}
                        >
                          -{result.pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Inflation Rate</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: accentColor }}
                        >
                          {inflation}% p.a.
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Years</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: accentColor }}
                        >
                          {years} yrs
                        </span>
                      </div>
                    </div>

                    <div className="ic-insight-box">
                      <Flame
                        size={16}
                        style={{ color: "#f59e0b", flexShrink: 0 }}
                      />
                      <p>
                        Every <strong>&#x20b9;1 Lakh</strong> sitting idle in a
                        savings account will buy only{" "}
                        <strong>
                          {inCrLakh(
                            100000 /
                              Math.pow(
                                1 + (+inflation || 6) / 100,
                                +years || 10,
                              ),
                          )}
                        </strong>{" "}
                        worth of goods in {years} years. Investing beats idle
                        savings every time.
                      </p>
                    </div>
                  </>
                )}

                {/* ── Real Returns ── */}
                {result.type === "real" && (
                  <>
                    <div className="ic-result-hero">
                      <div className="ic-result-col">
                        <div
                          className="ic-result-badge"
                          style={{ background: "#dbeafe", color: "#2563eb" }}
                        >
                          Nominal Return
                        </div>
                        <div
                          className="ic-result-big"
                          style={{ color: "#2563eb" }}
                        >
                          {inCrLakh(result.nominalFV)}
                        </div>
                      </div>
                      <div className="ic-result-arrow">&#8594;</div>
                      <div className="ic-result-col">
                        <div
                          className="ic-result-badge"
                          style={{ background: "#f0fdf4", color: "#16a34a" }}
                        >
                          Inflation-Adjusted
                        </div>
                        <div
                          className="ic-result-big"
                          style={{ color: "#16a34a" }}
                        >
                          {inCrLakh(result.realFV)}
                        </div>
                      </div>
                    </div>

                    <div className="ic-stat-row">
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Nominal Rate</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: "#2563eb" }}
                        >
                          {result.nominalRate.toFixed(1)}% p.a.
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Real Rate</span>
                        <span
                          className="ic-stat-val"
                          style={{
                            color: result.realRate > 0 ? "#16a34a" : "#ef4444",
                          }}
                        >
                          {result.realRate.toFixed(2)}% p.a.
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Inflation Erosion</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: "#ef4444" }}
                        >
                          -{inCrLakh(result.erosion)}
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Invested</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: accentColor }}
                        >
                          {inCrLakh(result.P)}
                        </span>
                      </div>
                    </div>

                    {result.realRate <= 0 && (
                      <div className="ic-insight-box ic-insight-box--warn">
                        <Flame
                          size={16}
                          style={{ color: "#ef4444", flexShrink: 0 }}
                        />
                        <p>
                          Your real return is{" "}
                          <strong style={{ color: "#ef4444" }}>
                            {result.realRate.toFixed(2)}%
                          </strong>{" "}
                          — inflation is outpacing your returns. Consider
                          higher-yield investments like equity mutual funds.
                        </p>
                      </div>
                    )}

                    {[
                      {
                        label: "Nominal Future Value",
                        val: result.nominalFV,
                        color: "#2563eb",
                      },
                      {
                        label: "Real Future Value",
                        val: result.realFV,
                        color: "#16a34a",
                      },
                    ].map((item) => (
                      <div key={item.label} className="ic-bar-group">
                        <div className="ic-bar-row">
                          <span className="ic-bar-label">{item.label}</span>
                          <span className="ic-bar-amt">
                            {inCrLakh(item.val)}
                          </span>
                        </div>
                        <div className="ic-bar-track">
                          <div
                            className="ic-bar-fill"
                            style={{
                              width: `${(item.val / result.nominalFV) * 100}%`,
                              background: `linear-gradient(90deg, ${item.color}, ${item.color}88)`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* ── Goal Inflation ── */}
                {result.type === "goal" && (
                  <>
                    <div className="ic-result-hero">
                      <div className="ic-result-col">
                        <div
                          className="ic-result-badge"
                          style={{ background: "#dcfce7", color: "#16a34a" }}
                        >
                          Today's Goal
                        </div>
                        <div
                          className="ic-result-big"
                          style={{ color: "#16a34a" }}
                        >
                          {inCrLakh(result.G)}
                        </div>
                      </div>
                      <div className="ic-result-arrow">&#8594;</div>
                      <div className="ic-result-col">
                        <div
                          className="ic-result-badge"
                          style={{ background: "#f0fdf4", color: "#059669" }}
                        >
                          Inflation-Adjusted Goal
                        </div>
                        <div
                          className="ic-result-big"
                          style={{ color: "#059669" }}
                        >
                          {inCrLakh(result.inflatedGoal)}
                        </div>
                      </div>
                    </div>

                    <div
                      className="ic-sip-highlight"
                      style={{
                        background: `linear-gradient(135deg, ${accentColor}10, ${accentEnd}18)`,
                        borderColor: `${accentColor}30`,
                      }}
                    >
                      <div>
                        <p className="ic-sip-label">Monthly SIP Required</p>
                        <p
                          className="ic-sip-amount"
                          style={{ color: accentColor }}
                        >
                          &#x20b9; {formatINR(Math.round(result.sip))} / month
                        </p>
                      </div>
                      <div className="ic-sip-divider" />
                      <div>
                        <p className="ic-sip-label">Total You'll Invest</p>
                        <p
                          className="ic-sip-amount"
                          style={{ color: "#0f172a" }}
                        >
                          {inCrLakh(result.totalInvested)}
                        </p>
                      </div>
                      <div className="ic-sip-divider" />
                      <div>
                        <p className="ic-sip-label">Investment Growth</p>
                        <p
                          className="ic-sip-amount"
                          style={{ color: "#16a34a" }}
                        >
                          +{inCrLakh(result.growth)}
                        </p>
                      </div>
                    </div>

                    <div className="ic-stat-row">
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">
                          Inflation Adjustment
                        </span>
                        <span
                          className="ic-stat-val"
                          style={{ color: "#ef4444" }}
                        >
                          +{inCrLakh(result.inflatedGoal - result.G)}
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Inflation Rate</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: "#ef4444" }}
                        >
                          {inflation}% p.a.
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Return Rate</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: accentColor }}
                        >
                          {returnRate}% p.a.
                        </span>
                      </div>
                      <div className="ic-stat-box">
                        <span className="ic-stat-lbl">Horizon</span>
                        <span
                          className="ic-stat-val"
                          style={{ color: accentColor }}
                        >
                          {years} yrs
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Tabular Breakdown (Uniform Table layout for all modes) ── */}
            {result.breakdown && (
              <div className="ic-card">
                <div
                  className="ic-card-bar"
                  style={{
                    background: `linear-gradient(90deg, ${accentColor}, ${accentEnd})`,
                  }}
                />
                <div className="ic-card-body">
                  <div className="ic-section-title">
                    Year-by-Year Schedule
                    <span className="ic-section-line" />
                  </div>

                  <div className="ic-table-wrap">
                    <table className="ic-table">
                      <thead>
                        <tr>
                          <th className="ic-th">Year</th>
                          {result.type === "future" && (
                            <>
                              <th className="ic-th">Projected Cost (₹)</th>
                              <th className="ic-th">Increase from Today (₹)</th>
                            </>
                          )}
                          {result.type === "present" && (
                            <>
                              <th className="ic-th">Purchasing Power (₹)</th>
                              <th className="ic-th">Value Lost (₹)</th>
                            </>
                          )}
                          {result.type === "real" && (
                            <>
                              <th className="ic-th">Nominal Value (₹)</th>
                              <th className="ic-th">Real Value (₹)</th>
                              <th className="ic-th">Inflation Erosion (₹)</th>
                            </>
                          )}
                          {result.type === "goal" && (
                            <>
                              <th className="ic-th">Inflated Goal (₹)</th>
                              <th className="ic-th">SIP Accumulated (₹)</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {result.breakdown.map((row, i) => (
                          <tr
                            key={row.year}
                            className={i % 2 === 0 ? "ic-tr-even" : "ic-tr-odd"}
                          >
                            <td className="ic-td ic-td-year">
                              Year {row.year}
                            </td>

                            {result.type === "future" && (
                              <>
                                <td className="ic-td ic-td-mono ic-td-bold ic-text-danger">
                                  {formatINR(row.cost)}
                                </td>
                                <td className="ic-td ic-td-mono">
                                  +{formatINR(row.increase)}
                                </td>
                              </>
                            )}

                            {result.type === "present" && (
                              <>
                                <td className="ic-td ic-td-mono ic-td-bold ic-text-success">
                                  {formatINR(row.pv)}
                                </td>
                                <td className="ic-td ic-td-mono ic-text-danger">
                                  -{formatINR(row.erosion)}
                                </td>
                              </>
                            )}

                            {result.type === "real" && (
                              <>
                                <td className="ic-td ic-td-mono">
                                  {formatINR(row.nominal)}
                                </td>
                                <td className="ic-td ic-td-mono ic-td-bold ic-text-success">
                                  {formatINR(row.real)}
                                </td>
                                <td className="ic-td ic-td-mono ic-text-danger">
                                  -{formatINR(row.erosion)}
                                </td>
                              </>
                            )}

                            {result.type === "goal" && (
                              <>
                                <td className="ic-td ic-td-mono ic-text-danger">
                                  {formatINR(row.goal)}
                                </td>
                                <td className="ic-td ic-td-mono ic-td-bold ic-text-success">
                                  {formatINR(row.sipBal)}
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ══ CTA ══ */}
        <div className="ic-cta-banner">
          <div className="ic-cta-text">
            <h3 className="ic-cta-title">
              Worried about inflation eating your wealth?
            </h3>
            <p className="ic-cta-sub">
              Our advisors will build an inflation-beating portfolio tailored to
              your timeline and goals.
            </p>
          </div>
          <Link to={getConsultationPath(WEB_LEAD_MODULES.CALC_INFLATION)} className="ic-cta-btn">
            Book Free Consultation &#8594;
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
}
